import jsPDF from "jspdf";
import "jspdf-autotable";
import { extractLogoColors, getDefaultColorPalette } from "./colorExtraction";
import { displaySchoolTerm } from "./schoolYearUtils";
import defaultLogo from "../data/logo/logo.png";

const fmt = (val) => {
  const num = Number(val) || 0;
  return `NGN ${num.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const generateInvoicePDF = async ({
  school,
  payment,
  studentAccount,
  totalPaid,
  returnBlob = false,
}) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const currentDate = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  // Extract brand colors from school logo
  let colors;
  try {
    const logoSource = school?.picture?.path || defaultLogo;
    colors = await extractLogoColors(logoSource);
  } catch {
    colors = getDefaultColorPalette();
  }

  // ── Header ────────────────────────────────────────────────────────────────
  const headerHeight = 28;
  const gradientSteps = 60;
  for (let i = 0; i < gradientSteps; i++) {
    const ratio = i / gradientSteps;
    const r = Math.floor(colors.gradientStart[0] * (1 - ratio) + colors.gradientEnd[0] * ratio);
    const g = Math.floor(colors.gradientStart[1] * (1 - ratio) + colors.gradientEnd[1] * ratio);
    const b = Math.floor(colors.gradientStart[2] * (1 - ratio) + colors.gradientEnd[2] * ratio);
    doc.setFillColor(r, g, b);
    doc.rect(
      0,
      i * (headerHeight / gradientSteps),
      pageWidth,
      headerHeight / gradientSteps + 0.5,
      "F",
    );
  }

  // School logo
  try {
    const logoSource = school?.picture?.path || defaultLogo;
    const isExternal = typeof logoSource === "string" && logoSource.startsWith("http");
    doc.addImage(logoSource, isExternal ? "PNG" : "JPG", margin, 6, 16, 13);
  } catch {
    /* skip logo if unavailable */
  }

  // School name + address
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text(school?.school_name || "School Name", margin + 20, 13);
  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  const address = school?.address?.main?.address_line || "";
  if (address) {
    const lines = doc.splitTextToSize(address, 80);
    doc.text(lines, margin + 20, 18);
  }

  // Invoice label (top-right)
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text("FEE INVOICE", pageWidth - margin, 13, { align: "right" });
  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  doc.text(`${displaySchoolTerm(payment?.term)} · ${payment?.session}`, pageWidth - margin, 19, {
    align: "right",
  });
  doc.text(`Issued: ${currentDate}`, pageWidth - margin, 24, { align: "right" });

  let y = headerHeight + 8;

  // ── Student + Parent info panels ─────────────────────────────────────────
  const panelW = (pageWidth - margin * 2 - 6) / 2;

  // Left panel — Student
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, panelW, 28, 2, 2, "F");
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, panelW, 28, 2, 2, "S");

  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  doc.setTextColor(148, 163, 184);
  doc.text("STUDENT", margin + 4, y + 6);

  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.setTextColor(30, 41, 59);
  const studentName = `${payment?.child?.firstname || ""} ${payment?.child?.lastname || ""}`.trim();
  doc.text(studentName || "-", margin + 4, y + 13);

  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  doc.setTextColor(100, 116, 139);
  doc.text(`Class: ${payment?.class?.class_name || "-"}`, margin + 4, y + 19);
  doc.text(`Level: ${payment?.class?.level_of_education || "-"}`, margin + 4, y + 24);

  // Right panel — Parent
  const rightX = margin + panelW + 6;
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(rightX, y, panelW, 28, 2, 2, "F");
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(rightX, y, panelW, 28, 2, 2, "S");

  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  doc.setTextColor(148, 163, 184);
  doc.text("PARENT / GUARDIAN", rightX + 4, y + 6);

  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.setTextColor(30, 41, 59);
  const parentName = `${payment?.user?.firstname || ""} ${payment?.user?.lastname || ""}`.trim();
  doc.text(parentName || "-", rightX + 4, y + 13);

  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  doc.setTextColor(100, 116, 139);
  if (payment?.user?.email) doc.text(payment.user.email, rightX + 4, y + 19);
  if (payment?.user?.phone) doc.text(payment.user.phone, rightX + 4, y + 24);

  y += 36;

  // The backend computes these directly on the payment (accounting for any unpaid
  // balance carried over from a previous term); fall back to the local calculation
  // further below for older responses that don't include them yet.
  const outstandingPayment = payment?.outstanding_payment || null;
  const outstandingRemaining =
    payment?.outstanding_remaining ??
    (outstandingPayment
      ? Math.max(
          0,
          Number(outstandingPayment.total_amount || 0) -
            Number(outstandingPayment.amount_paid || 0),
        )
      : 0);

  // ── Fee breakdown table ───────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(undefined, "bold");
  doc.setTextColor(30, 41, 59);
  doc.text("Fee Breakdown", margin, y);
  y += 4;

  const feeRows = [];

  // Tuition — no per-item amount_paid tracked on the backend for this line.
  feeRows.push(["Tuition Fee", fmt(payment?.class_price || 0), ""]);

  // Extra fees (add-ons) — each tracks its own amount_paid on the backend
  // (a payment allocation waterfall settles arrears first, then tuition,
  // then transport, then add-ons), so show it per item.
  (payment?.add_ons || []).forEach((addon) => {
    feeRows.push([addon.add_on_name, fmt(addon.add_on_price), fmt(addon.amount_paid || 0)]);
  });

  // Transport
  if (payment?.transport_add_on?.add_on_name) {
    const { add_on_name, add_on_price, amount_paid } = payment.transport_add_on;
    feeRows.push([`Transport - ${add_on_name}`, fmt(add_on_price), fmt(amount_paid || 0)]);
  }

  // Arrears
  (payment?.arrears || []).forEach((arrear) => {
    feeRows.push([arrear.name, fmt(arrear.amount), fmt(arrear.amount_paid || 0)]);
  });
  // Previous-term balance is a different invoice, not a component of this
  // one's fees — it gets its own callout box further down instead of a row
  // here (this is also what the removed "Type" column existed to flag).

  const [pr, pg, pb] = colors.primary || [99, 102, 241];

  doc.autoTable({
    startY: y,
    head: [["Description", "Amount", "Paid"]],
    body: feeRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: {
      fillColor: [pr, pg, pb],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 32, halign: "right", fontStyle: "bold" },
      2: { cellWidth: 32, halign: "right", fontSize: 8 },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  y = doc.lastAutoTable.finalY + 4;

  // ── Payment summary ───────────────────────────────────────────────────────
  const totalFee = Number(payment?.total_amount || 0);
  // `amountPaid` here is real cash only (summed from transaction/payment
  // history records) — the backend's `payment.amount_paid` field instead
  // bundles cash + any discount together, which is not what "Amount Paid"
  // should mean on the invoice.
  const amountPaid =
    totalPaid !== undefined ? Number(totalPaid) : Number(payment?.amount_paid || 0);
  const discount = Number(payment?.discount?.amount || 0);
  const invoicedTotal = Math.max(0, totalFee - discount);
  const currentBalance = Math.max(0, totalFee - amountPaid);
  const currentRemaining = payment?.current_remaining ?? currentBalance;
  const balance = payment?.total_due ?? currentRemaining + outstandingRemaining;
  const isPaid = payment?.is_fully_settled ?? balance <= 0;

  // Subtotal / Discount / Invoiced Total — only when there's a discount to explain.
  if (discount > 0) {
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(100, 116, 139);
    doc.text("Subtotal", margin, y + 4);
    doc.text(fmt(totalFee), pageWidth - margin, y + 4, { align: "right" });

    doc.setTextColor(21, 128, 61);
    doc.text("Discount", margin, y + 10);
    doc.text(`-${fmt(discount)}`, pageWidth - margin, y + 10, { align: "right" });

    doc.setDrawColor(226, 232, 240);
    doc.line(margin, y + 13, pageWidth - margin, y + 13);

    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.setTextColor(30, 41, 59);
    doc.text("Invoiced Total", margin, y + 19);
    doc.text(fmt(invoicedTotal), pageWidth - margin, y + 19, { align: "right" });

    y += 25;
  }

  const summaryBoxH = 22;
  const colW = (pageWidth - margin * 2) / 3;

  // Total fee
  doc.setFillColor(241, 245, 249);
  doc.roundedRect(margin, y, colW - 2, summaryBoxH, 2, 2, "F");
  doc.setFontSize(7);
  doc.setFont(undefined, "normal");
  doc.setTextColor(100, 116, 139);
  doc.text("TOTAL FEE", margin + 4, y + 7);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.setTextColor(30, 41, 59);
  doc.text(fmt(totalFee), margin + 4, y + 16);

  // Amount paid
  doc.setFillColor(240, 253, 244);
  doc.roundedRect(margin + colW, y, colW - 2, summaryBoxH, 2, 2, "F");
  doc.setFontSize(7);
  doc.setFont(undefined, "normal");
  doc.setTextColor(100, 116, 139);
  doc.text(discount > 0 ? "AMOUNT PAID (CASH)" : "AMOUNT PAID", margin + colW + 4, y + 7);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.setTextColor(21, 128, 61);
  doc.text(fmt(amountPaid), margin + colW + 4, y + 16);

  // Balance (total due — includes any balance carried over from a previous term)
  doc.setFillColor(isPaid ? 240 : 255, isPaid ? 253 : 251, isPaid ? 244 : 235);
  doc.roundedRect(margin + colW * 2, y, colW - 2, summaryBoxH, 2, 2, "F");
  doc.setFontSize(7);
  doc.setFont(undefined, "normal");
  doc.setTextColor(100, 116, 139);
  doc.text(outstandingRemaining > 0 ? "TOTAL DUE" : "BALANCE", margin + colW * 2 + 4, y + 7);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.setTextColor(isPaid ? 21 : 180, isPaid ? 128 : 83, isPaid ? 61 : 9);
  doc.text(fmt(balance), margin + colW * 2 + 4, y + 16);

  // Status badge
  const statusText = isPaid ? "FULLY PAID" : "OUTSTANDING";
  const [sr, sg, sb] = isPaid ? [21, 128, 61] : [180, 83, 9];
  const [br2, bg2, bb2] = isPaid ? [240, 253, 244] : [255, 251, 235];
  doc.setFillColor(br2, bg2, bb2);
  const badgeW = 30;
  const badgeH = 7;
  const badgeX = pageWidth - margin - badgeW;
  doc.roundedRect(badgeX, y + summaryBoxH + 3, badgeW, badgeH, 2, 2, "F");
  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  doc.setTextColor(sr, sg, sb);
  doc.text(statusText, badgeX + badgeW / 2, y + summaryBoxH + 8, { align: "center" });

  y += summaryBoxH + 14;

  // ── Previous term outstanding balance ────────────────────────────────────
  if (outstandingPayment && outstandingRemaining > 0) {
    const boxH = 24;
    doc.setFillColor(254, 242, 242);
    doc.setDrawColor(254, 202, 202);
    doc.roundedRect(margin, y, pageWidth - margin * 2, boxH, 3, 3, "FD");

    doc.setFontSize(8);
    doc.setFont(undefined, "bold");
    doc.setTextColor(185, 28, 28);
    doc.text("OUTSTANDING BALANCE FROM PREVIOUS TERM", margin + 6, y + 8);

    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(100, 116, 139);
    doc.text(
      `${displaySchoolTerm(outstandingPayment.term)} · ${outstandingPayment.session} - ${fmt(outstandingPayment.total_amount)} billed, ${fmt(outstandingPayment.amount_paid)} paid`,
      margin + 6,
      y + 15,
    );

    doc.setFontSize(7);
    doc.setFont(undefined, "normal");
    doc.setTextColor(100, 116, 139);
    doc.text("BALANCE", pageWidth - margin - 6, y + 8, { align: "right" });
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.setTextColor(220, 38, 38);
    doc.text(fmt(outstandingRemaining), pageWidth - margin - 6, y + 17, { align: "right" });

    y += boxH + 8;
  }

  // ── Payment account box ───────────────────────────────────────────────────
  if (studentAccount?.accountNumber) {
    doc.setFillColor(238, 242, 255);
    doc.setDrawColor(199, 210, 254);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 30, 3, 3, "FD");

    doc.setFontSize(8);
    doc.setFont(undefined, "bold");
    doc.setTextColor(67, 56, 202);
    doc.text("PAY INTO THIS ACCOUNT", margin + 6, y + 8);

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(30, 41, 59);

    const acctName = `${payment?.child?.firstname || ""} ${payment?.child?.lastname || ""}`.trim();
    doc.setFont(undefined, "bold");
    doc.text(`Account Number:`, margin + 6, y + 16);
    doc.setFont(undefined, "normal");
    doc.text(studentAccount.accountNumber, margin + 44, y + 16);

    doc.setFont(undefined, "bold");
    doc.text(`Account Name:`, margin + 6, y + 22);
    doc.setFont(undefined, "normal");
    doc.text(acctName || "-", margin + 44, y + 22);

    doc.setFont(undefined, "bold");
    doc.text(`Bank:`, margin + 6, y + 28);
    doc.setFont(undefined, "normal");
    doc.text("Globus Bank", margin + 44, y + 28);

    y += 38;
  }

  // ── Footer ────────────────────────────────────────────────────────────────
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);
  doc.setFontSize(7);
  doc.setFont(undefined, "normal");
  doc.setTextColor(148, 163, 184);
  doc.text(
    `${school?.school_name || ""} · Generated via Acceede · ${currentDate}`,
    pageWidth / 2,
    pageHeight - 8,
    { align: "center" },
  );

  const fileName = `Invoice_${studentName.replace(/\s+/g, "_")}_${payment?.term}_${payment?.session?.replace("/", "-")}.pdf`;

  if (returnBlob) {
    return { fileName, blob: doc.output("blob") };
  }

  doc.save(fileName);
};
